import { useEffect, useRef, useState } from 'react';
import {
  attachmentFileRefsSignature,
  type AttachmentDisplayURLParams,
} from '@/utils/agent-file-url';
import { hasNativeJSBridge } from '@/utils/bridge-runtime';
import { resolveAttachmentSource } from './attachment-source-resolver';

/**
 * Resolves attachment URLs for display. Data URLs are returned as-is.
 * Browser runtimes consume service-provided URLs directly.
 */
export function useResolvedUrl(
  params: AttachmentDisplayURLParams
): string | null {
  const [resolved, setResolved] = useState<string | null>(null);
  const {
    agentId,
    authRequired,
    auth_required,
    authScope,
    auth_scope,
    displayURL,
    displayUrl,
    display_url,
    downloadURL,
    downloadUrl,
    download_url,
    environmentId,
    filePath,
    fileRefs,
    preferSignedAgentFileShare,
    publicShareURL,
    publicShareUrl,
    public_share_url,
    signature,
    src,
    thumbnailURL,
    thumbnailUrl,
    thumbnail_url,
  } = params;
  const fileRefsKey = attachmentFileRefsSignature(fileRefs);
  const stableFileRefsRef = useRef(fileRefs);
  if (attachmentFileRefsSignature(stableFileRefsRef.current) !== fileRefsKey) {
    stableFileRefsRef.current = fileRefs;
  }

  useEffect(() => {
    let cancelled = false;
    const stableFileRefs = stableFileRefsRef.current;

    const resolve = async () => {
      const result = await resolveAttachmentSource(
        {
          agentId,
          authRequired,
          auth_required,
          authScope,
          auth_scope,
          displayURL,
          displayUrl,
          display_url,
          downloadURL,
          downloadUrl,
          download_url,
          environmentId,
          filePath,
          fileRefs: stableFileRefs,
          preferSignedAgentFileShare,
          publicShareURL,
          publicShareUrl,
          public_share_url,
          signature,
          src,
          thumbnailURL,
          thumbnailUrl,
          thumbnail_url,
        },
        {
          nativeAdapter: {
            resolveAuthenticatedSource: async source =>
              (await window.jsb?.MessagesBridge?.getStorageDownloadUrl(
                source
              )) ?? null,
          },
          nativeBridgeAvailable: hasNativeJSBridge(),
        }
      );
      if (cancelled) {
        return;
      }
      setResolved(result.status === 'resolved' ? result.value.source : null);
    };

    setResolved(null);
    void resolve();

    return () => {
      cancelled = true;
    };
  }, [
    agentId,
    authRequired,
    auth_required,
    authScope,
    auth_scope,
    displayURL,
    displayUrl,
    display_url,
    downloadURL,
    downloadUrl,
    download_url,
    environmentId,
    filePath,
    fileRefsKey,
    preferSignedAgentFileShare,
    publicShareURL,
    publicShareUrl,
    public_share_url,
    signature,
    src,
    thumbnailURL,
    thumbnailUrl,
    thumbnail_url,
  ]);

  return resolved;
}
